import { FC, useEffect, useState } from 'react';
import TrashIcon from '../../../../components/svgs/trashIcon';
import BtButton from '../../../bt-button/bt-button';
import styles from './SupplierMemberContainer.module.scss';
import { ReactComponent as EditIcon } from '../../../../assets/svg/editIcon.svg';
import { ReactComponent as UndoIcon } from '../../../../assets/svg/undoIcon.svg';
import { ReactComponent as VerifiedIcon } from '../../../../assets/svg/verifiedIcon.svg';
import { ReactComponent as InfoIcon } from '../../../../assets/svg/InfoIconLight.svg';
import { Member } from '../ContactInfoForm';
import Field from '../../../../components/shared/Field/Field';
import appState from '../../../../state/AppStateContainer';
import { useTranslation, Trans } from 'react-i18next';
import TooltipContainer from '../../../../components/shared/TooltipContainer/TooltipContainer';

type SupplierProfileType = {
  contacts?: Array<{
    _id: string,
    name: string,
    email: string,
    title: string,
    phone: string
  }>,
  otherEmailAddresses?: Array<{
    email: string,
    redirectTo?: string,
    comment?: string
  }>
};

type Props = {
  onRemove?: (value: boolean) => void,
  onChange: (values: Partial<Member>) => void,
  member: Member,
  supplierProfile?: SupplierProfileType,
  errors: Omit<Member, '_id'>,
  editMode: boolean,
  onEdit: (id: string) => void,
  isSupplierBlocked?: boolean,
}

const SupplierMemberContainer: FC<Props> = ({ onChange, onRemove, member, supplierProfile, errors, editMode, onEdit, isSupplierBlocked }) => {
  const [t] = useTranslation('supplierDatabase');
  const { name, email, title, phone, _id } = member;
  const [isRemoved, setIsRemoved] = useState(false);
  const isMemberTheLoggedInUser = _id === appState.getUserId();

  useEffect(() => {
    if (Object.values(errors || {}).some(value => value)) onEdit(_id);
  }, [errors]);

  const supplierProfileInfo = () => {
    if (!supplierProfile) return null;

    const { contacts, otherEmailAddresses } = supplierProfile;
    const contactMatch = contacts?.find(c => c.email === email);
    const otherEmailAddress = otherEmailAddresses?.find(e => e.email === email);

    if (contactMatch) {
      const diff = [
        contactMatch.name && contactMatch.name !== name && <div key="name">{t('addSupplier.profileInfo.diff.label.name', 'Namn')}: {contactMatch.name}</div>,
        contactMatch.title && contactMatch.title !== title && <div key="title">{t('addSupplier.profileInfo.diff.label.title', 'Titel')}: {contactMatch.title}</div>,
        contactMatch.phone && contactMatch.phone !== phone && <div key="phone">{t('addSupplier.profileInfo.diff.label.phone', 'Telefon')}: {contactMatch.phone}</div>,
      ].filter(x => x);

      return (
        <TooltipContainer renderReferenceComponent={(className, ref) => <VerifiedIcon className={className} ref={ref} style={{ marginRight: "8px" }} />}>
          <div style={{maxWidth: "512px", fontWeight: 400, fontSize: "14px", color: "var(--gray-700)" }}>
            {diff.length ? <>
              {t('addSupplier.profileInfo.diff.intro', 'E-postadressen matchar leverantörens egna uppgifter, men följande uppgifter skiljer sig:')}
              {diff}
            </> : t('addSupplier.profileInfo.diff.intro', 'Kontaktpersonen matchar leverantörens egna uppgifter.', { context: 'noDiff' })}
          </div>
        </TooltipContainer>
      );
    }

    if (otherEmailAddress) {
      const comment = otherEmailAddress.comment;
      if (otherEmailAddress.redirectTo) {
        const supplierProfileContact = contacts?.find(c => c._id === otherEmailAddress.redirectTo);
        const recommendedContact = contacts?.find(c => c.email === supplierProfileContact?.email);
        const name = recommendedContact?.name ?? supplierProfileContact?.name;

        if (name) {
          return (
            <TooltipContainer renderReferenceComponent={(className, ref) => <InfoIcon className={className} ref={ref} style={{ marginRight: '8px' }} />}>
              <div style={{maxWidth: '512px', fontWeight: 400, fontSize: '14px', color: 'var(--gray-700)' }}>
                {
                  comment ? (
                    <Trans t={t} i18nKey="addSupplier.profileInfo.otherEmailAddress.redirectTo_withComment" values={{ name, comment }}>
                      Leverantören rekommenderar: Kontakta <span style={{fontWeight: 500, color: 'var(--gray-900)'}}>{name}</span> i stället ({comment}).
                    </Trans>
                  ) : (
                    <Trans t={t} i18nKey="addSupplier.profileInfo.otherEmailAddress.redirectTo">
                      Leverantören rekommenderar: Kontakta <span style={{fontWeight: 500, color: 'var(--gray-900)'}}>{name}</span> i stället.
                    </Trans>
                  )
                }
              </div>
            </TooltipContainer>
          );
        }
      } else if (comment) {
        return (
          <TooltipContainer renderReferenceComponent={(className, ref) => <InfoIcon className={className} ref={ref} style={{ marginRight: '8px' }} />}>
            <div style={{maxWidth: '512px', fontWeight: 400, fontSize: '14px', color: 'var(--gray-700)' }}>
              <Trans t={t} i18nKey="addSupplier.profileInfo.otherEmailAddress.comment" values={{ comment }}>
                Meddelande från leverantören: <span style={{fontWeight: 500, color: 'var(--gray-900)'}}>{comment}</span>
              </Trans>
            </div>
          </TooltipContainer>
        );
      }
    }

    return null;
  };

  const supplierInfo = supplierProfileInfo();

  return (
    <div style={{display: "flex", alignItems: "center", width: "100%"}}>
      <div key="container" className={`${styles.container} ${isRemoved ? styles.isRemoved : ''} ${isSupplierBlocked && !editMode ? styles.blockedSupplier : ''}`}>
        <div className={styles.form}>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', gridRow: !title && !editMode? '1 / span 2' : '1', alignSelf: !title && !editMode ? 'center' : '' }}>
            <Field
              key="name"
              value={name}
              onChange={value => onChange({ name: value })}
              placeholder={t('supplierMember.placeholder.name', 'Namn*')}
              errorMessage={errors?.name}
              isEditMode={editMode}
              className={styles.boldText}
              style={{ marginRight: 0 }}
            />
            {supplierInfo && <span style={{ marginLeft: 8, display: 'flex', alignItems: 'center' }}>
              {supplierInfo}
            </span>}
          </div>
          <Field
            key="email"
            value={email}
            onChange={value => onChange({ email: value })}
            placeholder={t('supplierMember.placeholder.email', 'E-post*')}
            errorMessage={errors?.email}
            isEditMode={editMode}
            className={styles.lightText}
            style={{
              gridRow: !phone ? '1 / span 2' : '1',
              alignSelf: !phone ? 'center' : ''
            }}
          />
          {
            (title || editMode) && (
              <Field
                key="title"
                value={title}
                onChange={value => onChange({ title: value })}
                placeholder={t('supplierMember.placeholder.title', 'Titel')}
                errorMessage={errors?.title}
                isEditMode={editMode}
                className={styles.lightText}
              />
            )
          }
          {
            (phone || editMode) && (
              <Field
                key="phone"
                value={phone}
                onChange={value => onChange({ phone: value })}
                placeholder={t('supplierMember.placeholder.phone', 'Telefon')}
                errorMessage={errors?.phone}
                isEditMode={editMode}
                className={styles.lightText}
              />
            )
          }
        </div>
        <div className={`${styles.buttonsDiv} ${editMode || isRemoved ? styles.hideButton : ''}`}>
          <BtButton
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              onEdit(_id)
            }}
            color='white'
            size='xxs'
            leftIcon={
              <EditIcon style={{ width: '14px', fill: 'var(--gray-700)' }} />
            }
          />
          <BtButton
            color='white'
            size='xxs'
            disabled={!onRemove}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              if (onRemove) {
                onRemove(!isRemoved);
              }
              setIsRemoved(!isRemoved);
            }}
            style={{ visibility: isMemberTheLoggedInUser ? 'hidden' : 'visible' }}
            leftIcon={
              isRemoved ? (
                <UndoIcon style={{ width: '14px', fill: 'var(--gray-700)' }} />
              ) : (
                <TrashIcon size='14px' color='var(--gray-700)' />
              )
            }
          />
        </div>
      </div>
    </div>
  );
}

export default SupplierMemberContainer;
